import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { STORE_AVAILABILITY as dict } from '@/resources/dict'
import ALL_POC_STATUSES from '@/resources/enums/pocStatus'
import REASONS_TO_CLOSE from '@/resources/enums/reasonsToClose'

import RadioGroup from '@/components/generics/RadioGroup'
import TextArea from '@/components/generics/TextArea'
import Modal from '@/components/generics/Modal'
import Button from '@/components/generics/Button'
import LoadingButton from '@/components/generics/LoadingButton'

import { useOpenOrClosePoc } from '@/proxy/seller/hooks'
import * as S from './styles'
import { segmentClient } from '@/analytics/segmentClient'
import { pathNameToSegmentScreenName } from '@/resources/utils/analytics'
import { OwnerSquad } from '@/analytics/generated/segment'
import { useLocation } from 'react-router-dom'

const StoreClosingModal = ({
  show,
  handleCloseModal,
  handleSuccessfulClosing,
}) => {
  const { pathname } = useLocation()
  const textAreaRef = useRef()

  const [selectedReason, setSelectedReason] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [otherReasonText, setOtherReasonText] = useState('')
  const [storeClosingErrors, setStoreClosingErrors] = useState([])

  const closePoc = useOpenOrClosePoc()

  const customizeReasons = (reasons = []) =>
    reasons.map(reason => ({
      label: reason.label,
      value: reason.value,
      id: reason.value,
    }))

  const isOtherReason = reason => reason === REASONS_TO_CLOSE.OTHER

  const validateBeforeSubmit = successCallback => {
    const errors = []

    if (!selectedReason) {
      errors.push({ field: 'empty', message: dict.missingReason })
    }
    if (isOtherReason(selectedReason) && !otherReasonText) {
      errors.push({ field: 'otherReason', message: dict.missingReason })
    }
    if (!errors.length) {
      successCallback()
    } else {
      setStoreClosingErrors(errors)
    }
  }

  const selectReason = event => {
    const currentReason = event.selectedValue

    setSelectedReason(currentReason)
    setStoreClosingErrors([])
    setOtherReasonText('')
  }

  const closePartnerStore = () => {
    validateBeforeSubmit(async () => {
      const closingReason = isOtherReason(selectedReason)
        ? otherReasonText
        : selectedReason
      const closingReasonFormatted = {
        reason: closingReason,
      }

      try {
        setIsProcessing(true)

        const {
          openOrClosePoc: { success = false, poc = {}, errors = [] } = {},
        } = await closePoc.mutateAsync({
          input: {
            operation: ALL_POC_STATUSES.CLOSED,
            reasons: JSON.stringify(closingReasonFormatted),
          },
        })

        if (success) {
          setIsProcessing(false)
          handleSuccessfulClosing({
            poc,
            selectedReason,
            otherReasons: otherReasonText,
          })
        } else {
          setIsProcessing(false)
          setStoreClosingErrors(errors?.map(error => error?.message) ?? [])
        }
      } catch {
        setIsProcessing(false)
      }
    })
  }

  const renderErrorParagraph = error => (
    <S.ErrorParagraph key={error}>{error}</S.ErrorParagraph>
  )

  const showOtherReasonInput = isOtherReason(selectedReason)
  const serverErrors = storeClosingErrors.filter(error => !error?.field)
  const hasServerErrors = serverErrors.length > 0
  const userErrors = storeClosingErrors.filter(error => !!error?.field)
  const hasUserErrors = userErrors.length > 0

  useEffect(() => {
    if (show) {
      segmentClient.closeStoreModalViewed({
        screen_name: pathNameToSegmentScreenName(pathname),
        modal_name: 'close-store-modal',
        owner_squad: OwnerSquad.DtcZeOrders,
      })
    }
  }, [pathname, show])

  return (
    <Modal id="close-store-modal" show={show}>
      <S.ModalTitle>{dict.closeStoremodalTitle}</S.ModalTitle>
      <S.ModalContent>
        <S.CallToActionParagraph>
          <p id="closing-reason-message">{dict.closeStoreCallAction}</p>
        </S.CallToActionParagraph>
        <S.ReasonsToClose>
          <RadioGroup
            name="reason"
            options={
              Array.isArray(dict.reasons) && customizeReasons(dict.reasons)
            }
            selectedValue={selectedReason}
            direction="column"
            onChange={selectReason}
          />
          {showOtherReasonInput ? (
            <S.OtherReasonText>
              <TextArea
                id="close-store-modal-textarea"
                scheme={
                  storeClosingErrors.find(
                    ({ field }) => field === 'otherReason'
                  )
                    ? 'red'
                    : 'white'
                }
                value={otherReasonText}
                onChange={({ value }) => {
                  setOtherReasonText(value)
                  setStoreClosingErrors([])
                }}
                ref={textAreaRef}
                autoFocus
              />
            </S.OtherReasonText>
          ) : null}
          {hasUserErrors && (
            <div id="user-errors">
              {userErrors.map(error => renderErrorParagraph(error?.message))}
            </div>
          )}
        </S.ReasonsToClose>

        {hasServerErrors && (
          <S.ServerErrors id="server-errors">
            {serverErrors.map(renderErrorParagraph)}
          </S.ServerErrors>
        )}

        <S.ObservationParagraph>
          <p id="store-reopening-message">{dict.observationText}</p>
        </S.ObservationParagraph>

        <S.ModalActions>
          <Button outline onClick={handleCloseModal} id="close-modal-button">
            {dict.cancel}
          </Button>
          <LoadingButton
            isLoading={isProcessing}
            onClick={closePartnerStore}
            id="close-partner-store-button"
          >
            {dict.closeStore}
          </LoadingButton>
        </S.ModalActions>
      </S.ModalContent>
    </Modal>
  )
}

StoreClosingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleSuccessfulClosing: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
}

export default StoreClosingModal
